import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"./ui/select";
import { Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
{ value: "buffing", label: "Buffing & Polishing" },
{ value: "waxing", label: "Waxing & Protection" },
{ value: "interior", label: "Interior Detailing" },
{ value: "exterior", label: "Exterior Wash" },
{ value: "mold", label: "Mold & Mildew Removal" },
{ value: "full", label: "Full Detail Package" }];


export default function ContactForm() {
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    boat_length: "",
    service_type: "",
    details: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.service_type) {
      toast.error("Please fill in required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        email: formData.email,
        phone: formData.phone || null,
        boat_length: formData.boat_length || null,
        service_type: formData.service_type,
        details: formData.details || null
      };

      await axios.post(`${API}/contact`, payload);

      setSubmitted(true);
      toast.success("Quote request submitted successfully!");
      setFormData({
        email: "",
        phone: "",
        boat_length: "",
        service_type: "",
        details: ""
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-[#F8FAFC]"
      data-testid="contact-section">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Info */}
          <div data-testid="contact-info">
            <span
              className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mb-4"
              data-testid="contact-label">

              Get In Touch
            </span>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1E36] tracking-tight mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="contact-heading">

              Request a Quote
            </h2>
            <p
              className="text-[#64748B] leading-relaxed mb-8"
              data-testid="contact-description">Ready to make your boat shine? Fill out the form and we'll get back to you with a customized quote. We respond within 24 hours.


            </p>

            <div className="bg-[#0B1E36] p-8 text-white" data-testid="contact-cta-box">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}>

                Mobile Service Area
              </h3>
              <p className="text-white/80 text-sm">
                We service all marinas, docks, and private residences in the local area. Contact us to confirm we can reach your location.
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div
            className="bg-white border border-[#E2E8F0] p-6 lg:p-8"
            data-testid="contact-form-container">

            {submitted ?
            <div className="text-center py-12" data-testid="form-success">
                <div className="w-16 h-16 bg-[#FACC15] mx-auto mb-6 flex items-center justify-center">
                  <Send className="w-8 h-8 text-[#0B1E36]" />
                </div>
                <h3
                className="text-xl font-semibold text-[#0B1E36] mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}>

                  Thank You!
                </h3>
                <p className="text-[#64748B] mb-6">
                  We've received your request and will be in touch soon.
                </p>
                <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="rounded-none border-[#0B1E36] text-[#0B1E36] hover:bg-[#0B1E36] hover:text-white"
                data-testid="submit-another-btn">

                  Submit Another Request
                </Button>
              </div> :

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Email - Required */}
                <div>
                  <Label
                  htmlFor="email"
                  className="text-[#0F172A] font-medium mb-2 block">

                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="rounded-none border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]"
                  data-testid="input-email" />

                </div>

                {/* Phone - Optional */}
                <div>
                  <Label
                  htmlFor="phone"
                  className="text-[#0F172A] font-medium mb-2 block">

                    Phone <span className="text-[#64748B] text-sm">(optional)</span>
                  </Label>
                  <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="rounded-none border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]"
                  data-testid="input-phone" />

                </div>

                {/* Boat Length - Optional */}
                <div>
                  <Label
                  htmlFor="boat_length"
                  className="text-[#0F172A] font-medium mb-2 block">

                    Boat Length <span className="text-[#64748B] text-sm">(optional)</span>
                  </Label>
                  <Input
                  id="boat_length"
                  name="boat_length"
                  type="text"
                  value={formData.boat_length}
                  onChange={handleChange}
                  placeholder="e.g., 24 feet"
                  className="rounded-none border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]"
                  data-testid="input-boat-length" />

                </div>

                {/* Service Type - Required */}
                <div>
                  <Label
                  htmlFor="service_type"
                  className="text-[#0F172A] font-medium mb-2 block">

                    Type of Service <span className="text-red-500">*</span>
                  </Label>
                  <Select
                  value={formData.service_type}
                  onValueChange={handleServiceChange}
                  data-testid="select-service">

                    <SelectTrigger
                    className="rounded-none border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]"
                    data-testid="select-service-trigger">

                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent data-testid="select-service-content">
                      {serviceOptions.map((option) =>
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`service-option-${option.value}`}>

                          {option.label}
                        </SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Details - Optional */}
                <div>
                  <Label
                  htmlFor="details"
                  className="text-[#0F172A] font-medium mb-2 block">

                    Additional Details <span className="text-[#64748B] text-sm">(optional)</span>
                  </Label>
                  <Textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="Tell us more about your boat and what you're looking for..."
                  rows={4}
                  className="rounded-none border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36] resize-none"
                  data-testid="input-details" />

                </div>

                {/* Submit Button */}
                <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FACC15] text-[#0B1E36] hover:bg-[#EAB308] font-semibold py-6 rounded-none transition-all duration-300 btn-primary"
                data-testid="submit-btn">

                  {isSubmitting ?
                <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </> :

                <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Request
                    </>
                }
                </Button>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

}